import React, { useRef, useState } from "react";
import { X, Camera, User, Landmark, KeyRound, Mail, Save, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";
import { imgUrl } from "@/lib/catalog";

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl p-4 mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
    <p className="font-head text-[13px] font-bold mb-2.5 flex items-center gap-1.5"><Icon size={15} style={{ color: "var(--k-gold-deep)" }} /> {title}</p>
    {children}
  </div>
);
const inp = { background: "var(--k-paper)", border: "1px solid var(--k-line)" };

export default function AdminUserEditor({ user, onClose, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [prof, setProf] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  const [avatar, setAvatar] = useState(user.avatar || null);
  const [bank, setBank] = useState(user.bank || "");
  const [pwd, setPwd] = useState("");
  const [msg, setMsg] = useState({ subject: "", body: "" });
  const [wa, setWa] = useState("");
  const locked = user.isPrimaryAdmin;

  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const saveProfile = () => run(() => client.put(`/admin/users/${user.id}/profile`, prof), "تم حفظ المعلومات");
  const saveBank = () => run(() => client.put(`/admin/users/${user.id}/bank`, { bank }), "تم حفظ الحساب البنكي");
  const savePwd = () => { if (pwd.length < 6) return toast.error("كلمة المرور 6 أحرف على الأقل"); run(() => client.put(`/admin/users/${user.id}/password`, { password: pwd }), "تم تغيير كلمة المرور").then(() => setPwd("")); };
  const sendMsg = async () => {
    if (!msg.subject.trim() || !msg.body.trim()) return toast.error("الموضوع والنص مطلوبان");
    try {
      const r = await client.post(`/admin/users/${user.id}/email`, msg);
      if (r.data.email_sent) toast.success(`تم إرسال البريد إلى ${r.data.to}`);
      else if (r.data.email_configured) toast.error("تعذّر إرسال البريد الآن (ضغط على الخدمة) — أُنشئ إشعار داخل التطبيق. حاول مجدداً بعد قليل.");
      else toast.warning("البريد غير مُفعّل — أُنشئ إشعار داخل التطبيق فقط.");
      setMsg({ subject: "", body: "" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const sendWhatsApp = () => {
    const raw = (prof.phone || "").replace(/\D/g, "");
    if (!raw) return toast.error("لا يوجد رقم هاتف مسجّل لهذا الحساب");
    if (!wa.trim()) return toast.error("اكتب نص الرسالة أولاً");
    let num = raw;
    if (num.startsWith("00")) num = num.slice(2);
    else if (num.startsWith("0")) num = "218" + num.slice(1);
    else if (num.length <= 10 && !num.startsWith("218")) num = "218" + num;
    const url = `https://wa.me/${num}?text=${encodeURIComponent(wa.trim())}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("تم فتح واتساب");
  };
  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await client.put(`/admin/users/${user.id}/avatar`, { avatar: up.data.url });
      setAvatar(up.data.url); onChanged && onChanged();
      toast.success("تم تحديث الصورة");
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: "rgba(18,32,58,.55)" }} onClick={onClose}>
      <div className="w-full max-w-[440px] max-h-[88vh] overflow-y-auto no-scrollbar rounded-t-3xl p-5 k-rise" style={{ background: "var(--k-paper)" }} onClick={(e) => e.stopPropagation()} dir="rtl" data-testid="user-editor">
        <div className="flex justify-between items-center mb-4 sticky top-0 pb-2" style={{ background: "var(--k-paper)" }}>
          <h3 className="font-head text-[16px] font-bold">إدارة حساب: {user.name}</h3>
          <button data-testid="editor-close" onClick={onClose} className="k-tap w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><X size={16} /></button>
        </div>

        {locked && <p className="text-[12px] mb-3 rounded-xl px-3 py-2" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>هذا هو المدير الرئيسي — بعض الإجراءات محمية.</p>}

        <Section icon={Camera} title="الصورة الشخصية">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center text-white shrink-0" style={{ background: "var(--k-ink)" }}>
              {avatar ? <img src={imgUrl(avatar)} alt="avatar" className="w-full h-full object-cover" /> : <span className="font-head font-bold text-[20px]">{(user.name || "؟").charAt(0)}</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="editor-avatar-input" />
            <button data-testid="editor-avatar-btn" onClick={() => fileRef.current?.click()} className="k-tap flex-1 py-2.5 rounded-lg text-[12.5px] font-bold flex items-center justify-center gap-1.5" style={{ background: "var(--k-paper)", border: "1px dashed var(--k-line)" }}>
              <Camera size={15} /> {uploading ? "جارٍ الرفع…" : "رفع صورة"}
            </button>
          </div>
        </Section>

        <Section icon={User} title="المعلومات الأساسية">
          <label className="text-[12px] font-semibold block mb-1">الاسم</label>
          <input data-testid="editor-name" value={prof.name} onChange={(e) => setProf((p) => ({ ...p, name: e.target.value }))} className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={inp} />
          <label className="text-[12px] font-semibold block mb-1">البريد الإلكتروني</label>
          <input data-testid="editor-email" value={prof.email} onChange={(e) => setProf((p) => ({ ...p, email: e.target.value }))} className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2 font-num" style={{ ...inp, direction: "ltr", textAlign: "right" }} />
          <label className="text-[12px] font-semibold block mb-1">رقم الهاتف</label>
          <input data-testid="editor-phone" value={prof.phone} onChange={(e) => setProf((p) => ({ ...p, phone: e.target.value }))} className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-3 font-num" style={{ ...inp, direction: "ltr", textAlign: "right" }} />
          <button data-testid="editor-save-profile" onClick={saveProfile} className="k-tap w-full py-2.5 rounded-lg text-white font-bold text-[13px] flex items-center justify-center gap-1.5" style={{ background: "var(--k-ink)" }}><Save size={15} /> حفظ المعلومات</button>
        </Section>

        <Section icon={Landmark} title="الحساب البنكي">
          <textarea data-testid="editor-bank" value={bank} onChange={(e) => setBank(e.target.value)} rows={2} placeholder="اسم المصرف / رقم الحساب / IBAN" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={inp} />
          <button data-testid="editor-save-bank" onClick={saveBank} className="k-tap w-full py-2.5 rounded-lg text-white font-bold text-[13px]" style={{ background: "var(--k-ink)" }}>حفظ الحساب البنكي</button>
        </Section>

        <Section icon={KeyRound} title="تغيير كلمة المرور">
          <input data-testid="editor-password" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="كلمة مرور جديدة (6+ أحرف)" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={inp} />
          <button data-testid="editor-save-password" onClick={savePwd} className="k-tap w-full py-2.5 rounded-lg text-white font-bold text-[13px]" style={{ background: "var(--k-ink)" }}>تعيين كلمة المرور</button>
        </Section>

        <Section icon={Mail} title="إرسال رسالة إلى البريد الإلكتروني">
          <input data-testid="editor-msg-subject" value={msg.subject} onChange={(e) => setMsg((m) => ({ ...m, subject: e.target.value }))} placeholder="الموضوع" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={inp} />
          <textarea data-testid="editor-msg-body" value={msg.body} onChange={(e) => setMsg((m) => ({ ...m, body: e.target.value }))} rows={3} placeholder="نص الرسالة" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={inp} />
          <button data-testid="editor-msg-send" onClick={sendMsg} className="k-tap w-full py-2.5 rounded-lg font-bold text-[13px]" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}>إرسال البريد + إشعار</button>
        </Section>

        <Section icon={MessageCircle} title="إرسال رسالة إلى الهاتف (واتساب)">
          <p className="text-[11.5px] mb-2 leading-relaxed" style={{ color: "var(--k-ink-soft)" }}>
            {prof.phone ? <>سيتم إرسال الرسالة إلى الرقم المسجّل: <span className="font-num font-bold" style={{ direction: "ltr", unicodeBidi: "isolate" }}>{prof.phone}</span></> : "لا يوجد رقم هاتف مسجّل — أضف رقماً في المعلومات الأساسية أولاً."}
          </p>
          <textarea data-testid="editor-wa-body" value={wa} onChange={(e) => setWa(e.target.value)} rows={3} placeholder="نص رسالة الواتساب" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={inp} />
          <button data-testid="editor-wa-send" onClick={sendWhatsApp} disabled={!prof.phone} className="k-tap w-full py-2.5 rounded-lg font-bold text-[13px] flex items-center justify-center gap-1.5 text-white disabled:opacity-50" style={{ background: "var(--k-emerald)" }}>
            <MessageCircle size={15} /> إرسال عبر واتساب
          </button>
        </Section>
      </div>
    </div>
  );
}
